import { create } from 'zustand';
import type { AccountSelectionRequiredDto, UserProfileDto } from '../../domain/contracts/api.contracts';

/**
 * W03 — memory-only auth session (Key Architectural Invariant 1).
 * Access token is NEVER persisted to localStorage/sessionStorage.
 * `expiresInObservedAt` supports the client-side expiry heuristic; the exact expiry
 * claim name/units is an open question (api.contracts.ts — AuthSessionDto).
 */
interface AuthState {
  accessToken: string | null;
  user: UserProfileDto | null;
  /** Where the user was headed before being bounced to /login. */
  intendedRoute: string | null;
  /** epoch ms when the current token was issued — used only to proactively demo expiry. */
  tokenIssuedAt: number | null;

  /**
   * BD-30 — password verified but several apartment accounts exist: the picker is shown and NO session/dashboard
   * exists until the person selects one. Memory only, never persisted; cleared on selection, logout or expiry.
   */
  pendingSelection: AccountSelectionRequiredDto | null;

  /**
   * False only on a fresh page load, until the boot-time silent refresh (httpOnly cookie → new access token)
   * settles. Any setSession/clearSession flips it true, so login, logout and refresh-failure never trigger a
   * restore — only a reload does. Memory only.
   */
  restoreAttempted: boolean;

  setSession: (accessToken: string, user: UserProfileDto) => void;
  setPendingSelection: (selection: AccountSelectionRequiredDto | null) => void;
  setAccessToken: (accessToken: string) => void;
  clearSession: () => void;
  setIntendedRoute: (route: string | null) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  user: null,
  intendedRoute: null,
  tokenIssuedAt: null,
  pendingSelection: null,
  restoreAttempted: false,

  setSession: (accessToken, user) => set({ accessToken, user, tokenIssuedAt: Date.now(), pendingSelection: null, restoreAttempted: true }),
  setPendingSelection: (pendingSelection) => set({ pendingSelection }),
  setAccessToken: (accessToken) => set({ accessToken, tokenIssuedAt: Date.now() }),
  clearSession: () =>
    set({ accessToken: null, user: null, tokenIssuedAt: null, pendingSelection: null, restoreAttempted: true }),
  setIntendedRoute: (intendedRoute) => set({ intendedRoute }),
}));

/** Non-React access to the store (for axios interceptors). */
export const authStoreApi = useAuthStore;
